import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { forgotPassword, login, register } from './api'
import { errorText } from './ResetScreen'
import { meKey } from './useAuth'

type Props = {
  onDone: () => void
}

/**
 * Вход и регистрация — на одном экране, переключателем.
 *
 * Двумя страницами это делать не за что: поля почти те же, а человек, попав
 * не туда, обычно ищет вторую ссылку глазами по всей странице.
 *
 * Регистрация здесь ничего не открывает в самой игре: партия по ссылке
 * работает и без неё. Про это сказано прямо в подписи — иначе форма выглядит
 * как шлагбаум перед игрой.
 */
export function AuthScreen({ onDone }: Props) {
  const client = useQueryClient()
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login')
  const [nickname, setNickname] = useState('')
  const [email, setEmail] = useState('')
  const [identity, setIdentity] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')

  const enter = useMutation({
    mutationFn: () =>
      mode === 'login'
        ? login({ login: identity.trim(), password })
        : register({
            nickname: nickname.trim(),
            email: email.trim(),
            password,
            password_confirmation: repeat,
          }),
    onSuccess: async (me) => {
      client.setQueryData(meKey, me)
      onDone()
    },
  })

  // Забытый пароль: письмо со ссылкой. Отдельной вкладкой его не делаем —
  // это не способ входа, а починка входа.
  const forgot = useMutation({ mutationFn: () => forgotPassword(email.trim()) })

  /*
   * Переключение вкладки очищает форму.
   *
   * Поля у входа и регистрации разные, а введённое остаётся: человек,
   * начавший регистрацию и передумавший, возвращался ко входу с чужим
   * паролем в поле и старой ошибкой над кнопкой.
   */
  const switchTo = (next: 'login' | 'register' | 'forgot') => {
    setMode(next)
    setNickname('')
    setEmail('')
    setIdentity('')
    setPassword('')
    setRepeat('')
    enter.reset()
    forgot.reset()
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()

    if (mode === 'forgot') {
      forgot.mutate()

      return
    }

    enter.mutate()
  }

  return (
    <div className="lobby">
      <div className="hero">
        <h2 className="hero__title">
          {mode === 'login' ? 'Вход' : mode === 'register' ? 'Регистрация' : 'Забыли пароль'}
        </h2>
      </div>

      <div className="card">
        <div className="switch" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={mode === 'login' ? 'switch__on' : undefined}
            onClick={() => switchTo('login')}
          >
            Вход
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={mode === 'register' ? 'switch__on' : undefined}
            onClick={() => switchTo('register')}
          >
            Регистрация
          </button>
        </div>

        <form className="form" onSubmit={submit}>
          {mode === 'forgot' ? (
            <>
              <p className="muted">Отправим на почту ссылку для смены пароля.</p>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Почта"
                type="email"
                autoComplete="email"
                maxLength={255}
              />
              <button className="primary wide" type="submit" disabled={forgot.isPending}>
                Прислать ссылку
              </button>

              {forgot.isSuccess && <p className="banner banner--turn">{forgot.data}</p>}
              {forgot.isError && <p className="banner banner--error">{errorText(forgot.error)}</p>}

              <button className="ghost" type="button" onClick={() => switchTo('login')}>
                ← ко входу
              </button>
            </>
          ) : mode === 'login' ? (
            <input
              value={identity}
              onChange={(event) => setIdentity(event.target.value)}
              placeholder="Логин"
              autoComplete="username"
              maxLength={255}
            />
          ) : (
            <>
              <input
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                placeholder="Логин"
                autoComplete="username"
                maxLength={32}
              />
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Почта"
                type="email"
                autoComplete="email"
                maxLength={255}
              />
            </>
          )}

          {mode !== 'forgot' && (
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Пароль"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              maxLength={72}
            />
          )}

          {/* Повтор пароля: опечатку в закрытом точками поле иначе не
              заметить — она всплывёт при следующем входе. */}
          {mode === 'register' && (
            <input
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
              placeholder="Пароль ещё раз"
              type="password"
              autoComplete="new-password"
              maxLength={72}
            />
          )}

          {mode !== 'forgot' && (
            <button className="primary wide" type="submit" disabled={enter.isPending}>
              {mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
            </button>
          )}

          {mode === 'login' && (
            <button className="ghost" type="button" onClick={() => switchTo('forgot')}>
              забыли пароль?
            </button>
          )}
        </form>

        {enter.isError && <p className="banner banner--error">{errorText(enter.error)}</p>}
      </div>
    </div>
  )
}
